import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { authHeaders } from './Admin';

import { API_BASE } from '../apiBase';
const TOKEN_KEY = 'gardeno_admin_token';

const C = {
  bg: '#0a0a0b',
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
};

// Chart / accent colors
const BLUE = '#3B82F6';
const GREEN = '#34D399';
const TEAL = '#22b8cf';
const RED = '#F87171';

// ---------------- Types (mirror the API contract) ----------------
interface FlowRow { flow: string; devices: number }
interface FunnelStage { key: string; label: string; count: number; pct: number }
// branch: '' = shown to the whole cohort; 'care'/'identify' = screen exists on
// one branch only (all-flows view), so its bar covers a smaller cohort and it
// carries no drop chip.
interface StepRow {
  index: number; key: string; devices: number;
  pct: number; drop_pct: number; median_seconds: number;
  branch?: string;
}
interface SurveyAnswer { answer: string; count: number; pct: number; completion_rate: number }
interface SurveyCard { step_key: string; answers: SurveyAnswer[] }
interface DayPoint { date: string; sessions: number; completed: number }
// One exact drop-off list per branch (cohort = branch + pre-branch quitters).
interface StepsByFlow { flow: string; sessions: number; steps: StepRow[] }
interface Summary {
  sessions: number; completed: number; completion_rate: number; skipped: number;
  avg_completion_seconds: number; median_completion_seconds: number;
  accounts_created: number; paywall_shown: number; paywall_dismissed: number;
  trials_started: number; home_viewed: number; push_enabled: number;
  flows: FlowRow[];
  funnel: FunnelStage[];
  steps: StepRow[];
  steps_by_flow?: StepsByFlow[];
  surveys: SurveyCard[];
  by_day: DayPoint[];
}

// ---------------- Formatting helpers ----------------
function fmtNum(n: number | null | undefined): string {
  const v = typeof n === 'number' && isFinite(n) ? n : 0;
  return v.toLocaleString('en-US');
}
// fraction (0..1) -> "45%" (round)
function fmtPct(n: number | null | undefined): string {
  const v = typeof n === 'number' && isFinite(n) ? n : 0;
  return `${Math.round(v * 100)}%`;
}
function pctNum(n: number | null | undefined): number {
  const v = typeof n === 'number' && isFinite(n) ? n : 0;
  return v * 100;
}
// seconds -> "14m 33s" / "36s"
function fmtDuration(secs: number | null | undefined): string {
  const s = typeof secs === 'number' && isFinite(secs) ? Math.round(secs) : 0;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${rem}s`;
}
// pct of total, as rounded whole percent
function shareOf(part: number | null | undefined, total: number | null | undefined): string {
  const p = typeof part === 'number' && isFinite(part) ? part : 0;
  const t = typeof total === 'number' && isFinite(total) ? total : 0;
  if (t <= 0) return '0%';
  return `${Math.round((p / t) * 100)}%`;
}
// short axis label "Jul 1"
function shortDate(d: string): string {
  try {
    const dt = new Date(d.length <= 10 ? d + 'T00:00:00' : d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return d; }
}

// Humanize survey step keys
const SURVEY_TITLES: Record<string, string> = {
  ask_intent: 'Intent',
  ask_pain_point: 'Pain point',
  ask_plants_owned: 'Plants owned',
  ask_living_situation: 'Living situation',
  ask_time_available: 'Time available',
  ask_plant_interests: 'Plant interests',
  ask_id_pain: 'Identify pain',
  commitment: 'Commitment',
};
function surveyTitle(key: string): string {
  if (SURVEY_TITLES[key]) return SURVEY_TITLES[key];
  // fallback: strip ask_ prefix, title-case underscores
  const cleaned = key.replace(/^ask_/, '').replace(/_/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}
function humanizeAnswer(a: string): string {
  if (!a) return '—';
  const cleaned = a.replace(/_/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

// preset -> {from, to} in YYYY-MM-DD, or null for all time
function computeRange(preset: string): { from: string; to: string } | null {
  if (preset === 'all') return null;
  const to = new Date();
  const from = new Date();
  if (preset === '7') from.setDate(to.getDate() - 7);
  else if (preset === '30') from.setDate(to.getDate() - 30);
  else if (preset === '90') from.setDate(to.getDate() - 90);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
}

// ---------------- Small UI atoms ----------------
const cardStyle: React.CSSProperties = {
  background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20,
};
const selectStyle: React.CSSProperties = {
  padding: '9px 12px', borderRadius: 10, border: `1px solid ${C.border}`,
  background: C.panelAlt, color: C.text, fontSize: 14, cursor: 'pointer',
};

function KpiCard({ label, value, sub, valueColor }: { label: string; value: string; sub: string; valueColor?: string }) {
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 12, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, marginTop: 8, lineHeight: 1, color: valueColor || C.text }}>{value}</div>
      <div style={{ fontSize: 13, color: C.sub, marginTop: 8 }}>{sub}</div>
    </div>
  );
}

// Dark tooltip for recharts
function DarkTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px', fontSize: 13 }}>
      <div style={{ color: C.sub, marginBottom: 6 }}>{shortDate(label)}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ color: p.color || C.text, display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span style={{ textTransform: 'capitalize' }}>{String(p.name)}</span>
          <span style={{ fontWeight: 700 }}>{fmtNum(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------- Conversion funnel (SVG) ----------------
function ConversionFunnel({ stages, isMobile }: { stages: FunnelStage[]; isMobile: boolean }) {
  const H = 140;               // container height
  const MIN_H = 4;             // never render a zero-height sliver
  const max = Math.max(1, ...stages.map((s) => s.count));
  const n = stages.length;

  // heights per stage (0..H)
  const heights = stages.map((s) => Math.max(MIN_H, (s.count / max) * H));

  return (
    <div>
      {/* stage labels row */}
      <div style={{ display: 'flex', marginTop: 18 }}>
        {stages.map((s) => (
          <div key={s.key} style={{ flex: 1, textAlign: 'center', padding: '0 6px' }}>
            <div style={{ fontSize: isMobile ? 11 : 12, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</div>
            <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, marginTop: 6, lineHeight: 1 }}>{fmtNum(s.count)}</div>
            <div style={{ marginTop: 6 }}>
              <span style={{ display: 'inline-block', background: '#0E7490', color: '#CFFAFE', padding: '2px 9px', borderRadius: 100, fontSize: 11, fontWeight: 700 }}>{fmtPct(s.pct)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* funnel shape */}
      <div style={{ marginTop: 14, height: H }}>
        <svg width="100%" height={H} viewBox={`0 0 ${n * 100} ${H}`} preserveAspectRatio="none" style={{ display: 'block' }}>
          <defs>
            <linearGradient id="funnelFill" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#2DD4BF" />
              <stop offset="100%" stopColor="#0E7490" />
            </linearGradient>
          </defs>
          {(() => {
            // Each stage's label sits at the centre of its column, so the band
            // has to be that thickness AT the centre and taper between centres.
            // Flat caps on both ends keep the first and last stages visible —
            // previously the shape stopped at the second-to-last centre, which
            // left the final stage with no band under it at all.
            const cx = (i: number) => (i + 0.5) * 100;
            const band = (x0: number, x1: number, h0: number, h1: number, key: string, i: number) => {
              const yTop0 = (H - h0) / 2;
              const yTop1 = (H - h1) / 2;
              const pts = `${x0},${yTop0} ${x1},${yTop1} ${x1},${yTop1 + h1} ${x0},${yTop0 + h0}`;
              return (
                <polygon
                  key={key}
                  points={pts}
                  fill="url(#funnelFill)"
                  opacity={Math.max(0.4, 0.9 - i * 0.08)}
                />
              );
            };

            const parts = [band(0, cx(0), heights[0], heights[0], 'cap-start', 0)];
            for (let i = 0; i < n - 1; i++) {
              parts.push(band(cx(i), cx(i + 1), heights[i], heights[i + 1], `seg-${i}`, i));
            }
            parts.push(
              band(cx(n - 1), n * 100, heights[n - 1], heights[n - 1], 'cap-end', n - 1)
            );
            return parts;
          })()}
        </svg>
      </div>
    </div>
  );
}

// ---------------- Main component ----------------
export default function OnboardingTab({ token, isMobile }: { token: string; isMobile: boolean }) {
  // filters
  const [preset, setPreset] = useState('all');   // 7 | 30 | 90 | all — DEFAULT all
  const [flow, setFlow] = useState('all');        // all | care | identify
  const [variant, setVariant] = useState('all');  // all | a | b
  const [excludeAdmins, setExcludeAdmins] = useState(true);
  // Which drop-off list to show: one exact list per branch, or the combined view.
  const [stepView, setStepView] = useState<'care' | 'identify' | 'all'>('care');

  // data
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // stale-response guard
  const reqId = useRef(0);

  const on401 = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    window.location.reload();
  }, []);

  const load = useCallback(async () => {
    const myReq = ++reqId.current;
    setLoading(true);
    setError(null);
    const range = computeRange(preset);
    const params: Record<string, string> = {
      flow, variant, exclude_admins: String(excludeAdmins),
    };
    if (range) { params.from = range.from; params.to = range.to; }
    const q = new URLSearchParams(params).toString();
    try {
      const res = await fetch(`${API_BASE}/admin/onboarding/summary?${q}`, { headers: authHeaders(token) });
      if (myReq !== reqId.current) return; // stale
      if (res.status === 401 || res.status === 403) { on401(); return; }
      if (!res.ok) { setError(`Server error (${res.status}).`); return; }
      const d = await res.json();
      if (myReq !== reqId.current) return; // stale
      setSummary((d.data ?? d) as Summary);
    } catch {
      if (myReq !== reqId.current) return;
      setError('Could not reach the server.');
    } finally {
      if (myReq === reqId.current) setLoading(false);
    }
  }, [token, preset, flow, variant, excludeAdmins, on401]);

  useEffect(() => { load(); }, [load]);

  const s = summary;
  const hasData = !!s && (s.sessions > 0 || (s.funnel && s.funnel.length > 0) || (s.steps && s.steps.length > 0));

  // flow option labels with device counts
  const flowDevices = (key: string): number => {
    const row = (s?.flows ?? []).find((f) => f.flow === key);
    return row ? row.devices : 0;
  };

  // ---- Loading early state ----
  if (loading && !s) {
    return <div style={{ marginTop: 40, color: C.sub, fontSize: 15 }}>Loading onboarding…</div>;
  }

  const overallCompletion = s?.completion_rate ?? 0;

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* 1. HEADER ROW */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>Onboarding Analytics</h1>
          <p style={{ color: C.sub, marginTop: 6, fontSize: 15, maxWidth: 560 }}>
            Drop-off, survey answers, and conversion from the in-app onboarding flow.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 0, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden', flexShrink: 0 }}>
          {[
            { l: 'Sessions', v: fmtNum(s?.sessions) },
            { l: 'Completion Rate', v: fmtPct(s?.completion_rate) },
          ].map((it, i) => (
            <div key={it.l} style={{ padding: '14px 20px', borderLeft: i ? `1px solid ${C.border}` : 'none', minWidth: 92 }}>
              <div style={{ fontSize: 11, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>{it.l}</div>
              <div style={{ fontSize: 20, fontWeight: 800, marginTop: 6, lineHeight: 1 }}>{it.v}</div>
            </div>
          ))}
        </div>
      </div>

      {error && <p style={{ color: RED, marginTop: 16 }}>{error}</p>}

      {/* 2. FILTER BAR */}
      <div style={{ marginTop: 22, ...cardStyle, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <select value={preset} onChange={(e) => setPreset(e.target.value)} style={selectStyle}>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="all">All time</option>
        </select>
        <select value={flow} onChange={(e) => setFlow(e.target.value)} style={selectStyle}>
          <option value="all">All flows{s ? ` (${fmtNum(flowDevices('care') + flowDevices('identify'))})` : ''}</option>
          <option value="care">Care &amp; Both path{s ? ` (${fmtNum(flowDevices('care'))})` : ''}</option>
          <option value="identify">Identify path{s ? ` (${fmtNum(flowDevices('identify'))})` : ''}</option>
        </select>
        <select value={variant} onChange={(e) => setVariant(e.target.value)} style={selectStyle}>
          <option value="all">All variants</option>
          <option value="a">Paywall A</option>
          <option value="b">Paywall B</option>
        </select>

        {/* Exclude admins pill toggle */}
        <button
          onClick={() => setExcludeAdmins((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 9, padding: '8px 14px', borderRadius: 100,
            border: `1px solid ${excludeAdmins ? C.accent : C.border}`, background: C.panelAlt,
            color: C.text, fontSize: 13, cursor: 'pointer', fontWeight: 600,
          }}
        >
          <span style={{
            width: 30, height: 17, borderRadius: 100, background: excludeAdmins ? C.accent : C.border,
            position: 'relative', transition: 'background .15s', flexShrink: 0,
          }}>
            <span style={{
              position: 'absolute', top: 2, left: excludeAdmins ? 15 : 2, width: 13, height: 13,
              borderRadius: '50%', background: '#fff', transition: 'left .15s',
            }} />
          </span>
          Exclude admins
        </button>
      </div>

      {!hasData && !loading ? (
        <div style={{ ...cardStyle, marginTop: 22, textAlign: 'center', padding: '48px 20px', color: C.sub }}>
          No onboarding data yet. Try widening the date range or clearing filters.
        </div>
      ) : (
        <>
          {/* 3. KPI CARDS */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(6, 1fr)', gap: 14, marginTop: 22 }}>
            <KpiCard label="Sessions" value={fmtNum(s?.sessions)} sub={`${fmtNum(s?.completed)} completed`} />
            <KpiCard label="Completion rate" value={fmtPct(s?.completion_rate)} sub={`${fmtNum(s?.skipped)} skipped`} valueColor={GREEN} />
            <KpiCard label="Avg completion time" value={fmtDuration(s?.avg_completion_seconds)} sub={`median ${fmtDuration(s?.median_completion_seconds)}`} />
            <KpiCard label="Accounts created" value={fmtNum(s?.accounts_created)} sub={`${shareOf(s?.accounts_created, s?.sessions)} of sessions`} />
            <KpiCard label="Trials started" value={fmtNum(s?.trials_started)} sub={`${shareOf(s?.trials_started, s?.sessions)} of sessions`} />
            <KpiCard label="Push enabled" value={fmtNum(s?.push_enabled)} sub={`${shareOf(s?.push_enabled, s?.sessions)} of sessions`} />
          </div>

          {/* 4. CONVERSION FUNNEL */}
          <div style={{ ...cardStyle, marginTop: 18 }}>
            <div style={{ fontSize: 17, fontWeight: 800 }}>Conversion funnel</div>
            <div style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>Session milestones: started → finished questions → paywall → trial.</div>
            {(s?.funnel && s.funnel.length > 0) ? (
              <ConversionFunnel stages={s.funnel} isMobile={isMobile} />
            ) : (
              <div style={{ fontSize: 13, color: C.sub, marginTop: 16 }}>No funnel data.</div>
            )}
          </div>

          {/* 5. PAGE-BY-PAGE DROP-OFF */}
          {(() => {
            const byFlow = (fl: string) => (s?.steps_by_flow ?? []).find((b) => b.flow === fl);
            const view = stepView !== 'all' ? byFlow(stepView) : undefined;
            const stepRows: StepRow[] = stepView === 'all' ? (s?.steps ?? []) : (view?.steps ?? []);
            const viewSessions = stepView === 'all' ? s?.sessions : view?.sessions;
            const viewLabel = stepView === 'care' ? 'Care & Both path' : stepView === 'identify' ? 'Identify path' : 'Combined';
            return (
          <div style={{ ...cardStyle, marginTop: 18 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800 }}>Page-by-page drop-off</div>
                <div style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>
                  Sessions that ever reached each page. The taper between adjacent steps is the drop-off on that page.
                  {stepView !== 'all' && ` Showing ${viewLabel} — ${fmtNum(viewSessions)} sessions entered this path; percentages are of those users. The pre-branch drop is visible in Combined.`}
                </div>
              </div>
              <div style={{ display: 'flex', background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 3, flexShrink: 0 }}>
                {([['care', 'Care & Both'], ['identify', 'Identify path'], ['all', 'Combined']] as const).map(([k, label]) => (
                  <button key={k} onClick={() => setStepView(k)} style={{
                    background: stepView === k ? C.panel : 'transparent',
                    color: stepView === k ? C.text : C.sub,
                    border: stepView === k ? `1px solid ${C.border}` : '1px solid transparent',
                    borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}>{label}</button>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              {stepRows.length === 0 ? (
                <div style={{ fontSize: 13, color: C.sub }}>No step data for this path.</div>
              ) : (
                stepRows.map((st) => {
                  const barPct = Math.max(1, Math.min(100, pctNum(st.pct)));
                  const dropPct = pctNum(st.drop_pct);
                  const showDrop = dropPct >= 3;
                  const strongDrop = dropPct >= 10;
                  const showTime = st.median_seconds > 0;
                  return (
                    <div key={st.key} style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                      <div style={{
                        width: isMobile ? 96 : 150, flexShrink: 0, fontSize: 13,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {st.index}. {st.key}
                      </div>
                      <div style={{ flex: 1, height: 18, background: C.panelAlt, borderRadius: 6, overflow: 'hidden' }}>
                        <div style={{ width: `${barPct}%`, height: '100%', background: TEAL, borderRadius: 6, opacity: st.branch ? 0.45 : 1 }} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, justifyContent: 'flex-end', minWidth: isMobile ? 96 : 160 }}>
                        {st.branch && (
                          <span style={{
                            background: C.panelAlt, color: C.sub, padding: '2px 8px', borderRadius: 100,
                            fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', border: `1px solid ${C.border}`,
                          }}>{st.branch === 'care' ? 'care & both' : st.branch} path</span>
                        )}
                        <span style={{ fontSize: 13, color: C.sub, fontWeight: 600, width: 40, textAlign: 'right' }}>{fmtPct(st.pct)}</span>
                        {showDrop && (
                          <span style={{
                            background: strongDrop ? '#3a1414' : '#2a1616',
                            color: strongDrop ? '#F87171' : '#E9928F',
                            padding: '2px 8px', borderRadius: 100, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                            border: strongDrop ? '1px solid #5a2020' : 'none',
                          }}>−{Math.round(dropPct)}%</span>
                        )}
                        {showTime && (
                          <span style={{
                            background: C.panelAlt, color: C.sub, padding: '2px 8px', borderRadius: 100,
                            fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', border: `1px solid ${C.border}`,
                          }}>{st.median_seconds}s</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
            );
          })()}

          {/* 6. SURVEY INSIGHTS */}
          <div style={{ marginTop: 26 }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>Survey answers</div>
            <div style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>What users told us during onboarding — and how each answer converts.</div>
            {(s?.surveys ?? []).length === 0 ? (
              <div style={{ ...cardStyle, marginTop: 16, color: C.sub, fontSize: 13 }}>No survey data.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 14, marginTop: 16 }}>
                {(s?.surveys ?? []).map((card) => {
                  const maxAns = Math.max(1, ...card.answers.map((a) => a.pct));
                  return (
                    <div key={card.step_key} style={cardStyle}>
                      <div style={{ fontSize: 15, fontWeight: 800 }}>{surveyTitle(card.step_key)}</div>
                      <div style={{ marginTop: 6 }}>
                        {card.answers.length === 0 ? (
                          <div style={{ fontSize: 13, color: C.sub, marginTop: 10 }}>No answers.</div>
                        ) : (
                          card.answers.map((a) => {
                            const good = a.completion_rate >= overallCompletion;
                            return (
                              <div key={a.answer} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
                                <div style={{ width: isMobile ? 90 : 120, flexShrink: 0, fontSize: 13, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{humanizeAnswer(a.answer)}</div>
                                <div style={{ flex: 1, height: 8, background: C.panelAlt, borderRadius: 100, overflow: 'hidden' }}>
                                  <div style={{ width: `${Math.max(2, Math.min(100, (a.pct / maxAns) * 100))}%`, height: '100%', background: TEAL, borderRadius: 100 }} />
                                </div>
                                <div style={{ width: 44, textAlign: 'right', fontSize: 12, color: C.sub, flexShrink: 0 }}>{fmtNum(a.count)}</div>
                                <div style={{ flexShrink: 0 }}>
                                  <span style={{
                                    background: good ? '#123524' : '#3a1414',
                                    color: good ? '#4ADE80' : '#F87171',
                                    padding: '2px 8px', borderRadius: 100, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                                  }}>{fmtPct(a.completion_rate)} finish</span>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 7. SESSIONS OVER TIME */}
          {(s?.by_day && s.by_day.length >= 2) && (
            <div style={{ ...cardStyle, marginTop: 18 }}>
              <div style={{ fontSize: 17, fontWeight: 800 }}>Sessions over time</div>
              <div style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>Daily onboarding sessions started and completed.</div>
              <div style={{ height: 280, marginTop: 16 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={s.by_day} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fill: C.sub, fontSize: 12 }} axisLine={{ stroke: C.border }} tickLine={false} minTickGap={20} />
                    <YAxis tick={{ fill: C.sub, fontSize: 12 }} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
                    <Tooltip content={<DarkTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                    <Bar dataKey="sessions" name="sessions" fill={BLUE} radius={[3, 3, 0, 0]} maxBarSize={26} />
                    <Line type="monotone" dataKey="completed" name="completed" stroke={GREEN} strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
